import React, { useState } from 'react';

import {
  Box,
  ChipProps,
  ClickAwayListener,
  Popper,
  Stack,
  TextFieldProps,
} from '@mui/material';

import AutocompleteElement from './AutoCompleteElement';
import { FormInputProps } from '../Interfaces/FormInputProps';

interface IProps extends FormInputProps {
  isMultiselect?: boolean;
  chipStyle?: any;
  control: any;
  renderCustomComponent?: (value: string, props?: ChipProps) => React.ReactNode;
  loading?: boolean;
  showCheckbox?: boolean;
  defaultValue?: any;
  required?: boolean;
  labelKey?: string;
  valueKey?: string;
  countKey?: string;
  textFieldProps?: Omit<TextFieldProps, 'name' | 'required' | 'label'>;
  fixedValues?: any;
  readOnly?: boolean;
  canCreateNew?: boolean | ((typedValue: string, options: any[]) => boolean);
  createNewText?: string;
  canSelectAll?: boolean;
  addNewValue?: (value: string) => void;
  CustomInput?: React.ElementType;
  disableCloseOnSelect?: boolean;
  disableClearable?: boolean;
  isFilterCustomComponent?: boolean;
  isShowDropDown?: boolean;
  renderOption?: () => any;
  disableOptionCondition?: (option: any) => boolean;
  renderProgress?: (value: number) => React.ReactElement;
  renderAction?: (value: string | any) => React.ReactElement;
  groupedOptions?: boolean;
  isGroupByOptions?: boolean;
  groupValue?: string;
  inputValue?: string;
  onInputChange?: (event: any, value: string, reason: string) => void;
  onDropdownClose?: (inputValue: string, options: any[]) => void;
  onFocus?: any;
  limitTags?: number;
  onBlur?: () => void;
  optionsSx?: any;
}

export default function RISelectComponent({
  isMultiselect = false,
  label,
  options,
  name,
  loading = false,
  showCheckbox = true,
  control,
  renderCustomComponent,
  required = false,
  fixedValues,
  readOnly,
  canCreateNew,
  createNewText,
  canSelectAll,
  CustomInput,
  addNewValue,
  disableCloseOnSelect,
  disableClearable,
  isShowDropDown,
  renderOption,
  disableOptionCondition,
  isFilterCustomComponent = false,
  groupedOptions,
  isGroupByOptions,
  groupValue,
  inputValue,
  onInputChange,
  onDropdownClose,
  onFocus,
  limitTags,
  onBlur,
  optionsSx,
  disabled,
  ...props
}: IProps) {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // for tables multiselect flickering fixes
  const CustomPopper = (popperProps: any) => (
    <Popper
      {...popperProps}
      placement="bottom-start"
      modifiers={[
        {
          name: 'preventOverflow',
          options: {
            rootBoundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'offset',
          options: {
            offset: [-8, 6],
          },
        },
      ]}
    />
  );

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        <AutocompleteElement
          readOnly={readOnly}
          label={label}
          name={name}
          multiple={isMultiselect}
          options={options || []}
          matchId
          showCheckbox={showCheckbox}
          control={control}
          loading={loading}
          required={required}
          fixedValues={fixedValues}
          canCreateNew={canCreateNew}
          createNewText={createNewText}
          canSelectAll={canSelectAll}
          addNewValue={addNewValue}
          CustomInput={CustomInput}
          isGroupByOptions={isGroupByOptions}
          groupedOptions={groupedOptions}
          groupValue={groupValue}
          isFilterCustomComponent={isFilterCustomComponent}
          optionsSx={optionsSx}
          autocompleteProps={{
            disabled: disabled,
            disableCloseOnSelect: disableCloseOnSelect,
            disableClearable: disableClearable,
            inputValue: inputValue,
            onInputChange: onInputChange,
            renderOption,
            getOptionDisabled: disableOptionCondition,
            freeSolo: isShowDropDown,
            onFocus: onFocus,
            onBlur: onBlur,
            limitTags: limitTags,
            open: open,
            onOpen: handleOpen,
            onClose: () => {
              handleClose();
              if (onDropdownClose) {
                onDropdownClose(inputValue || '', options || []);
              }
            },
            renderTags: (value: any[], getTagProps: any) =>
              renderCustomComponent ? (
                isFilterCustomComponent ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'nowrap',
                      gap: 0.5,
                      height: '55px',
                      alignItems: 'center',
                      overflow: 'scroll',
                      '&::-webkit-scrollbar': {
                        display: 'none',
                      },
                    }}
                  >
                    {value.map((option: any, index) => (
                      <React.Fragment key={index}>
                        {renderCustomComponent(option, {
                          ...getTagProps({ index }),
                        })}
                      </React.Fragment>
                    ))}
                  </Box>
                ) : (
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                    overflow="auto"
                    key={Math.random()}
                  >
                    {value.map((option: any, index) => (
                      <React.Fragment key={index}>
                        {renderCustomComponent(option, {
                          ...getTagProps({ index }),
                        })}
                      </React.Fragment>
                    ))}
                  </Stack>
                )
              ) : null,
            ...(isFilterCustomComponent && {
              PopperComponent: CustomPopper,
            }),
          }}
          {...props}
        />
      </div>
    </ClickAwayListener>
  );
}
